import { useEffect, useState } from "react"
import Cropper, { type Area, type Point } from "react-easy-crop"
import { useTranslation } from "react-i18next"

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Slider } from "@/components/ui/slider"
import { getCroppedBlob } from "@/lib/crop-image"
import { cn } from "@/lib/utils"

/**
 * OKS-697 / OKS-768 — Generic cover crop step, shared across every
 * crop-on-upload surface (mission covers, masterclass covers, …).
 *
 * Opened by a cover *field* when the admin picks or replaces an image.
 * Presents a `react-easy-crop` viewport LOCKED to a caller-supplied
 * aspect ratio (`aspectRatio` + the matching Tailwind `aspectClass`) so
 * the framed area is exactly what every display surface renders — no
 * server-side center-crop, no arbitrary band (client-crop-authoritative).
 * On *Apply* the crop rectangle is rasterised to a `Blob` (via the
 * mockable `getCroppedBlob` canvas seam) and handed back through
 * `onApply`; the field stages it into the hidden multipart file input and
 * the parent Inertia `<Form>` submits it unchanged.
 *
 * Extracted from OKS-697's `MissionCoverCropper` (OKS-768) so mission and
 * masterclass share ONE cropper rather than a drifting fork. The ratio,
 * aspect class, testId prefix, and i18n copy are all injected — every
 * consumer keeps its own single-source-of-truth ratio constant
 * (`lib/mission-cover.ts`, `lib/masterclass-cover.ts`) and its own copy
 * keys, so `MissionCoverCropper` stays a byte-identical thin wrapper and
 * OKS-697's specs are unaffected.
 *
 * Presentation is the Crew `ResponsiveDialog` idiom: a centered `Dialog`
 * at ≥640px and a full-height bottom `Sheet` below it — one responsive
 * surface, focus-trapped, ESC-cancellable, ≥44px footer.
 *
 * Crew form rules hold: this is a crop widget, NOT a form — no
 * react-hook-form / zod. The only form on the page is the parent Inertia
 * `<Form>`; validation errors still render from its `errors` render-prop.
 *
 * States (UX Spec §3): while the source decodes, the viewport shows a
 * ratio-locked skeleton and *Apply* is disabled; a file that can't decode
 * shows an inline `role="alert"` message and the dialog stays open so the
 * admin can cancel and pick another.
 */

type CropStatus = "loading" | "ready" | "error"

// Zoom bounds for the slider + pinch. 1 = fit (react-easy-crop default),
// 3× is enough to reframe a tight subject without exposing the padding
// that `objectFit="contain"` letterboxes in for very tall/wide sources.
const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 0.01

/**
 * i18n KEYS for the cropper chrome. Each consumer passes its own namespace
 * (mission passes `missions.media.cover_crop_*`, masterclass passes
 * `masterclasses.form.cover.crop_*`) so the shared component carries no
 * hard-coded copy and every surface keeps its own EN/FR strings.
 */
export interface CoverCropperLabels {
  /** Dialog title key. */
  title: string
  /** Dialog description key. */
  description: string
  /** Zoom slider label key. */
  zoom: string
  /** Cancel button key. */
  cancel: string
  /** Apply button key. */
  apply: string
  /** Undecodable-source inline `role="alert"` message key. */
  decodeError: string
}

export interface CoverCropperProps {
  /** Open state — controlled by the parent field. */
  open: boolean
  /** Object URL of the picked source image. Null when nothing staged. */
  imageSrc: string | null
  /** Source MIME type; passed through to the canvas export so PNG/WEBP
   *  sources keep their type (defaults to JPEG inside the seam). */
  mimeType?: string
  /** Numeric crop ratio (width / height) — feeds `react-easy-crop`'s
   *  `aspect` prop. Kept in lockstep with `aspectClass`. */
  aspectRatio: number
  /** Tailwind aspect-ratio utility (literal from the caller's cover-ratio
   *  module) applied to the viewport so the framed box matches the ratio
   *  the crop math enforces. */
  aspectClass: string
  /** i18n keys for the dialog chrome. */
  labels: CoverCropperLabels
  /** Called with the cropped Blob on *Apply*. */
  onApply: (blob: Blob) => void
  /** Called on *Cancel* / ESC / overlay dismiss — no file staged. */
  onCancel: () => void
  /** testId prefix; every hook is `${testId}-…`. */
  testId?: string
}

export function CoverCropper({
  open,
  imageSrc,
  mimeType,
  aspectRatio,
  aspectClass,
  labels,
  onApply,
  onCancel,
  testId = "cover-crop",
}: CoverCropperProps) {
  const { t } = useTranslation()

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(MIN_ZOOM)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [status, setStatus] = useState<CropStatus>("loading")
  const [applying, setApplying] = useState(false)

  // Reset transient crop state and probe the source each time a new image
  // is staged (or the dialog reopens). The probe drives the skeleton →
  // ready transition and the decode-error path independently of
  // react-easy-crop's own internal load.
  useEffect(() => {
    if (!open || !imageSrc) return
    setCrop({ x: 0, y: 0 })
    setZoom(MIN_ZOOM)
    setCroppedAreaPixels(null)
    setStatus("loading")

    let active = true
    const probe = new window.Image()
    probe.addEventListener("load", () => {
      if (active) setStatus("ready")
    })
    probe.addEventListener("error", () => {
      if (active) setStatus("error")
    })
    probe.src = imageSrc

    return () => {
      active = false
    }
  }, [open, imageSrc])

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    setApplying(true)
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mimeType)
      onApply(blob)
    } catch {
      // A raster failure is treated like a decode failure — surface the
      // inline message and keep the dialog open to retry/cancel.
      setStatus("error")
    } finally {
      setApplying(false)
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) onCancel()
  }

  const canApply = status === "ready" && Boolean(croppedAreaPixels) && !applying
  // OKS-857 — portrait ratios need a height-capped viewport on desktop so the
  // dialog + footer fit (see the viewport `className` note below).
  const isPortrait = aspectRatio < 1

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent
        data-testid={`${testId}-dialog`}
        className="sm:max-w-lg"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{t(labels.title)}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {t(labels.description)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="flex flex-col gap-4 p-4 sm:p-0">
          {/* Ratio-locked crop viewport. react-easy-crop fills this box.

              OKS-857 — for PORTRAIT ratios (aspect < 1) the width-driven box
              gets too tall on a centered desktop Dialog (a 9:16 area at the
              512px dialog width is ~910px tall — taller than a 720px viewport
              — pushing the footer off-screen). On `sm+` we therefore drive the
              portrait viewport by HEIGHT (`h-[60dvh]`, width derived from the
              aspect) and `self-center` it so flex-stretch doesn't force it back
              to full width. Landscape ratios (3:2 mission/masterclass/cover)
              keep the original width-driven box byte-for-byte — the height cap
              is portrait-only. On mobile (bottom Sheet) both keep `w-full`. */}
          <div
            className={cn(
              "bg-muted relative w-full overflow-hidden rounded-lg",
              aspectClass,
              isPortrait && "sm:h-[60dvh] sm:w-auto sm:self-center",
            )}
            data-testid={`${testId}-viewport`}
          >
            {status === "error" ? (
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <p
                  role="alert"
                  className="text-destructive text-center text-sm"
                  data-testid={`${testId}-decode-error`}
                >
                  {t(labels.decodeError)}
                </p>
              </div>
            ) : (
              <>
                {status === "loading" ? (
                  <Skeleton
                    className="absolute inset-0 h-full w-full rounded-lg"
                    data-testid={`${testId}-skeleton`}
                  />
                ) : null}
                {imageSrc ? (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspectRatio}
                    objectFit="contain"
                    minZoom={MIN_ZOOM}
                    maxZoom={MAX_ZOOM}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_area, areaPixels) =>
                      setCroppedAreaPixels(areaPixels)
                    }
                  />
                ) : null}
              </>
            )}
          </div>

          {/* Keyboard-operable zoom (also serves non-pinch desktop). */}
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${testId}-zoom`}>{t(labels.zoom)}</Label>
            <Slider
              id={`${testId}-zoom`}
              data-testid={`${testId}-zoom`}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onValueChange={(next) =>
                setZoom(typeof next === "number" ? next : next[0])
              }
              disabled={status !== "ready"}
              aria-label={t(labels.zoom)}
            />
          </div>
        </div>

        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            data-testid={`${testId}-cancel`}
          >
            {t(labels.cancel)}
          </Button>
          <Button
            type="button"
            onClick={() => void handleApply()}
            disabled={!canApply}
            data-testid={`${testId}-apply`}
          >
            {t(labels.apply)}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default CoverCropper
